import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtNum } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { useProducts, productOptions, opt, BILLING_CYCLES } from "@/lib/lookups";

export default function Customers() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: rows } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: products } = useProducts();

  const save = useMutation({
    mutationFn: async (p) => {
      const payload = { ...p, products: p.product_id ? [p.product_id] : (editing?.products || []) };
      delete payload.product_id;
      return editing ? api.put(`/customers/${editing.id}`, payload) : api.post("/customers", payload);
    },
    onSuccess: () => { qc.invalidateQueries(); toast.success("Customer saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const fields = [
    { name: "legal_name", label: "Legal Company Name", full: true },
    { name: "trading_name", label: "Trading Name" },
    { name: "abn", label: "ABN" },
    { name: "primary_contact", label: "Primary Contact" },
    { name: "email", label: "Email", type: "email" },
    { name: "phone", label: "Phone" },
    { name: "product_id", label: "Primary Product", type: "select", options: productOptions(products) },
    { name: "subscription_plan", label: "Subscription Plan", placeholder: "e.g. Fleet" },
    { name: "status", label: "Status", type: "select", options: opt(["lead", "trial", "pilot", "active", "paused", "former"]), default: "lead" },
    { name: "billing_cycle", label: "Billing Cycle", type: "select", options: BILLING_CYCLES, default: "monthly" },
    { name: "subscription_value", label: "Subscription Value (ex-GST / cycle)", type: "number", step: "0.01" },
    { name: "vessels", label: "Vessels", type: "number" },
    { name: "users", label: "Users", type: "number" },
    { name: "normal_value", label: "Normal Commercial Value", type: "number", step: "0.01" },
    { name: "contract_value", label: "Contract Value", type: "number", step: "0.01" },
    { name: "start_date", label: "Start Date", type: "date" },
    { name: "founding_customer", label: "Founding customer", type: "switch" },
    { name: "pilot_customer", label: "Pilot customer", type: "switch" },
    { name: "address", label: "Address", type: "textarea", full: true },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];

  const columns = [
    { key: "legal_name", header: "Customer", render: (r) => (
      <div><div className="font-semibold text-slate-100">{r.legal_name}</div>{r.trading_name && <div className="text-xs text-slate-500">{r.trading_name}</div>}</div>
    ) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "vessels", header: "Vessels", align: "right", mono: true, render: (r) => fmtNum(r.vessels) },
    { key: "users", header: "Users", align: "right", mono: true, render: (r) => fmtNum(r.users) },
    { key: "billing_cycle", header: "Cycle", render: (r) => <span className="capitalize text-xs text-slate-400">{r.billing_cycle}</span> },
    { key: "subscription_value", header: "Subscription", align: "right", mono: true, render: (r) => fmtMoney(r.subscription_value) },
    { key: "founding_customer", header: "Founding", render: (r) => r.founding_customer ? <StatusBadge status="pilot" label="Founding" /> : <span className="text-slate-600">—</span> },
    { key: "actions", header: "", align: "right", render: (r) => (
      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); setEditing({ ...r, product_id: r.products?.[0] || "" }); setOpen(true); }} data-testid={`edit-customer-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
    ) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Customers" subtitle="Central NavScora commercial customer registry" testId="customers-header">
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="add-customer-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Customer
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={rows} testId="customers-table"
        onRowClick={(r) => navigate(`/customers/${r.id}`)}
        rowTestId="customer-row"
        empty={{ title: "No customers yet", message: "Add your first customer to begin tracking commercial relationships." }} />
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Customer" : "Add Customer"} fields={fields} initial={editing} onSubmit={(p) => save.mutateAsync(p).then(() => true).catch(() => false)} testId="customer-form" />
    </div>
  );
}
